/**
 * ViewportContextFrame — a camera-viewport-style border with corner ticks and a
 * centered label, drawn over the 3D viewport to signal an active editing context
 * (Pages, Properties state groups, …).
 *
 * Shared so the Pages overlay and the state-strip frame stay visually identical:
 * only the accent color and the trailing suffix differ. Renders nothing but the
 * frame chrome (pointer-events: none) — the host positions it and supplies its
 * own controls (toggle buttons, etc.).
 */

import React from 'react';

export interface ViewportContextFrameProps {
    /** Primary label (the edited root/container name — or the active page). */
    label: string;
    /** Optional trailing suffix shown dimmed after the label (e.g. "· editing states"). */
    suffix?: string;
    /** Accent color for the border + corner ticks. Defaults to a soft blue. */
    accent?: string;
    /**
     * Optional prev/next buttons flanking the header label (the Pages frame
     * shows the active page name with ◀ ▶ navigation). Interactive — the
     * frame is otherwise pointer-transparent.
     */
    nav?: {
        onPrev: () => void;
        onNext: () => void;
        prevTitle?: string;
        nextTitle?: string;
    };
}

const DEFAULT_ACCENT = '120, 170, 210'; // rgb triple — alpha applied per use

const CORNER = 14;
const CORNER_THICK = 2;

const cornerBase: React.CSSProperties = {
    position: 'absolute',
    width: `${CORNER}px`,
    height: `${CORNER}px`,
    pointerEvents: 'none',
};

/** The four corner ticks, parameterized by accent color. */
function cornerStyles(accent: string): React.CSSProperties[] {
    const c = `${CORNER_THICK}px solid rgba(${accent}, 0.55)`;
    return [
        { ...cornerBase, top: '8px', left: '8px', borderTop: c, borderLeft: c, borderRadius: '6px 0 0 0' },
        { ...cornerBase, top: '8px', right: '8px', borderTop: c, borderRight: c, borderRadius: '0 6px 0 0' },
        { ...cornerBase, bottom: '8px', left: '8px', borderBottom: c, borderLeft: c, borderRadius: '0 0 0 6px' },
        { ...cornerBase, bottom: '8px', right: '8px', borderBottom: c, borderRight: c, borderRadius: '0 0 6px 0' },
    ];
}

const headerStyle: React.CSSProperties = {
    position: 'absolute', top: '14px', left: '50%', transform: 'translateX(-50%)',
    display: 'flex', alignItems: 'center', gap: '4px', pointerEvents: 'none',
};
const labelStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px',
    background: 'rgba(15, 25, 40, 0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    borderRadius: '4px', whiteSpace: 'nowrap',
};
const navButtonStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '24px', height: '24px', padding: 0,
    background: 'rgba(15, 25, 40, 0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
    color: 'rgba(255, 255, 255, 0.75)', fontSize: '10px', pointerEvents: 'auto',
};
const labelTextStyle: React.CSSProperties = {
    color: 'rgba(255, 255, 255, 0.9)', fontSize: '11px', fontWeight: 500,
    letterSpacing: '0.03em', fontFamily: 'system-ui, -apple-system, sans-serif',
};

export function ViewportContextFrame({ label, suffix, accent = DEFAULT_ACCENT, nav }: ViewportContextFrameProps) {
    const borderStyle: React.CSSProperties = {
        position: 'absolute', inset: '8px', border: `1.5px solid rgba(${accent}, 0.25)`,
        borderRadius: '6px', pointerEvents: 'none',
    };
    const suffixStyle: React.CSSProperties = {
        color: `rgba(${accent}, 0.85)`, fontSize: '11px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
    };
    return (
        <>
            <div style={borderStyle} />
            {cornerStyles(accent).map((c, i) => <div key={i} style={c} />)}
            <div style={headerStyle}>
                {nav && (
                    <button style={navButtonStyle} onClick={nav.onPrev} title={nav.prevTitle ?? 'Previous page'}>◀</button>
                )}
                <div style={labelStyle}>
                    <span style={labelTextStyle}>{label}</span>
                    {suffix && <span style={suffixStyle}>{suffix}</span>}
                </div>
                {nav && (
                    <button style={navButtonStyle} onClick={nav.onNext} title={nav.nextTitle ?? 'Next page'}>▶</button>
                )}
            </div>
        </>
    );
}
